import { io, type Socket } from 'socket.io-client';
import { SOCKET_NAMESPACE } from '@docsystem/shared';
import { authStore, getDisplayToken } from './auth-store';

const SOCKET_URL = import.meta.env.VITE_SOCKET_URL ?? 'http://localhost:3001';

export type ConnectionStatus = 'connecting' | 'connected' | 'reconnecting' | 'disconnected';

type StatusListener = (status: ConnectionStatus) => void;

let socket: Socket | null = null;
let currentStatus: ConnectionStatus = 'disconnected';
const statusListeners = new Set<StatusListener>();

function setStatus(status: ConnectionStatus): void {
  currentStatus = status;
  for (const listener of statusListeners) listener(status);
}

export function onConnectionStatus(listener: StatusListener): () => void {
  statusListeners.add(listener);
  listener(currentStatus);
  return () => statusListeners.delete(listener);
}

export function getConnectionStatus(): ConnectionStatus {
  return currentStatus;
}

/**
 * One socket per tab, reused across every hook that needs it.
 *
 * `randomizationFactor` matters more than it looks: without jitter, forty TVs in a
 * hospital all reconnect on the same tick after a backend restart and hammer the
 * API in lockstep.
 */
export function getSocket(options: { asDisplay?: boolean } = {}): Socket {
  // Mirrors the REST client: a provisioned kiosk uses its own credential; a staff
  // member previewing the display falls back to their session.
  const token = options.asDisplay
    ? (getDisplayToken() ?? authStore.accessToken)
    : authStore.accessToken;

  if (socket) {
    const auth = socket.auth as { token?: string } | undefined;
    if (auth?.token !== token) {
      socket.auth = { token };
      if (socket.connected) {
        socket.disconnect().connect();
      }
    }
    return socket;
  }

  socket = io(`${SOCKET_URL}${SOCKET_NAMESPACE}`, {
    auth: { token },
    transports: ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: 1_000,
    reconnectionDelayMax: 10_000,
    randomizationFactor: 0.5,
    timeout: 20_000,
    autoConnect: true,
  });

  socket.on('connect', () => setStatus('connected'));
  socket.on('disconnect', () => setStatus('reconnecting'));
  socket.io.on('reconnect_attempt', () => setStatus('reconnecting'));
  socket.io.on('reconnect', () => setStatus('connected'));
  socket.io.on('error', () => setStatus('disconnected'));

  setStatus('connecting');
  return socket;
}

export function disconnectSocket(): void {
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
    socket = null;
  }
  setStatus('disconnected');
}

/** Re-authenticates the existing connection after a token refresh. */
export function refreshSocketAuth(): void {
  if (!socket) return;
  socket.auth = { token: authStore.accessToken };
  socket.disconnect().connect();
}
